import "~/styles/login.css"

import { ConfigProvider, theme } from "antd"
import {
  BookOpen,
  FileText,
  Mail,
  MessageCircle,
  Presentation,
  Target
} from "lucide-react"

const SANITY_CDN = "https://cdn.sanity.io/images/8o5kdxko/production"

// `h` is a per-logo optical height (px); these SVGs have very different aspect
// ratios, so a single shared height makes some look oversized (eClerx) and
// others tiny (Wipro, L&T).
// `ml`/`mr` are negative margins that cancel each SVG's baked-in transparent
// horizontal padding (measured per logo) so the ink-to-ink spacing across the
// row is a uniform 28px (the flex gap). Without this, HCL Tech's ~31px of side
// padding opens visibly larger gaps around it.
const LOGOS = [
  {
    name: "Tech Mahindra",
    src: `${SANITY_CDN}/22f3c5a377a0c83b08438221d5becbdf9419b5f9-2500x708.svg`,
    h: 26,
    ml: -13,
    mr: -3
  },
  {
    name: "Infosys",
    src: `${SANITY_CDN}/de458ab0af076750b8902e5c4fcfa30cf97d4a45-2500x923.svg`,
    h: 26,
    ml: -4,
    mr: -4
  },
  {
    name: "Fujitsu",
    src: `${SANITY_CDN}/24cb0564dc38536df0dd444564fa164f06a819b4-2500x1176.svg`,
    h: 30,
    ml: 0,
    mr: 0
  },
  {
    name: "HCL Tech",
    src: `${SANITY_CDN}/c8e87a21645e91cb71098dd739ee9ea414849569-2500x265.svg`,
    h: 16,
    ml: -31,
    mr: -31
  },
  {
    name: "Wipro",
    src: `${SANITY_CDN}/e5ed3125839c6536269c7a405c5925fb1a09ccf1-2500x1618.svg`,
    h: 42,
    ml: -6,
    mr: -6
  },
  {
    name: "L&T Technology Services",
    src: `${SANITY_CDN}/b61ee9c5029bc898c249fe7c14bf5b4a6893fd99-1543x232.svg`,
    h: 30,
    ml: 0,
    mr: 0
  },
  {
    name: "eClerx",
    src: `${SANITY_CDN}/d2f3b58eb0ca7dce7ff76e014a3546f94f77a67f-127x35.svg`,
    h: 20,
    ml: 0,
    mr: 0
  }
]

const FEATURES = [
  {
    Icon: Target,
    title: "Account prioritisation",
    subtitle: "Know who to pitch and call this week."
  },
  {
    Icon: BookOpen,
    title: "Pre-meeting briefings",
    subtitle: "A full brief, ready before every call."
  },
  {
    Icon: Mail,
    title: "Personalised outreach",
    subtitle: "Contextualized messaging, written to convert."
  },
  {
    Icon: FileText,
    title: "RFP response generation",
    subtitle: "First draft ready in minutes, not days."
  },
  {
    Icon: MessageCircle,
    title: "On-demand knowledge access",
    subtitle: "Ask anything, get a sourced answer."
  },
  {
    Icon: Presentation,
    title: "Client-ready pitch decks",
    subtitle: "Account-specific and ready to present."
  }
]

const RightPanelContent = () => {
  return (
    <div className="w-full max-w-[540px] flex flex-col my-auto login-right-content">
      <h2 className="login-right-heading font-bold text-white leading-tight mb-8 lg:mb-10">
        Everything you need to win more deals
      </h2>

      <div className="flex flex-col gap-5 lg:gap-6 mb-8 lg:mb-10">
        {FEATURES.map(({ Icon, title, subtitle }) => (
          <div key={title} className="flex items-start gap-4">
            <div className="login-feature-icon flex-shrink-0 flex items-center justify-center rounded-xl bg-white/[0.05] border border-white/[0.08]">
              <Icon
                className="login-feature-icon-glyph text-white/80"
                strokeWidth={1.8}
              />
            </div>
            <div className="min-w-0">
              <p className="login-feature-title font-semibold text-white leading-snug">
                {title}
              </p>
              <p className="login-feature-subtitle text-white/45 leading-snug mt-0.5">
                {subtitle}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="pt-6 lg:pt-8 border-t border-white/[0.06]">
        <p className="login-feature-subtitle text-white/40 mb-3 lg:mb-4">
          {"Trusted by "}
          <span className="text-white/70 font-semibold">2,000+</span>
          {" revenue teams"}
        </p>
        <div className="overflow-hidden login-marquee-wrapper">
          <div className="login-marquee">
            {[...LOGOS, ...LOGOS].map((logo, i) => (
              <img
                key={i}
                src={logo.src}
                alt={logo.name}
                loading="lazy"
                style={{
                  height: logo.h,
                  marginLeft: logo.ml,
                  marginRight: logo.mr
                }}
                className="login-logo-img"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

interface AuthLayoutProps {
  children: React.ReactNode
  footer?: React.ReactNode
}

export default function AuthLayout({ children, footer }: AuthLayoutProps) {
  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <div className="flex h-screen w-screen overflow-hidden">
        <div className="flex-1 flex flex-col bg-[#fafafe] login-left-panel">
          <div className="login-left-panel-halo" />
          <div className="login-left-panel-grid" />
          <div className="relative z-10 flex flex-1 flex-col items-center px-6 md:px-12 lg:px-20 py-6 lg:py-10 overflow-y-auto">
            <div className="flex-shrink-0 mb-6 lg:mb-10 max-w-[440px] w-full">
              <img
                src="/orbitshift_logo.svg"
                alt="OrbitShift"
                className="login-logo"
              />
            </div>
            <div className="flex-1 flex flex-col justify-center max-w-[440px] w-full">
              {children}
            </div>
            {footer && (
              <div className="flex-shrink-0 w-full flex justify-end mt-4 lg:mt-6">
                {footer}
              </div>
            )}
          </div>
        </div>

        <section className="hidden md:flex w-1/2 min-w-[50%] max-w-[50%] login-right-panel">
          <div className="login-right-panel-halo" />
          <div className="login-right-panel-grid" />
          <div className="relative z-10 flex flex-col items-center px-6 lg:px-14 py-6 lg:py-10 w-full h-full overflow-y-auto">
            <RightPanelContent />
          </div>
        </section>
      </div>
    </ConfigProvider>
  )
}
